import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    arrayRemove,
    arrayUnion,
    collection,
    deleteField,
    doc,
    writeBatch,
} from 'firebase/firestore';

import { db } from '../../../firebase.js';
import {
    areCompanyMembershipPermissionsEqual,
    normalizeCompanyMembershipPermissionEntry,
    normalizeIdList,
    serializeCompanyMembershipPermissionEntry,
} from '../setupShared.js';

const createEmptyCompanyDraft = () => ({
    name: '',
    testCompany: false,
    active: true,
    userIds: [],
    permissionsByUserId: {},
});

// State and writes for the Virksomheder tab. The company list itself stays on the page,
// which is why saving hands back an updated list rather than refetching one.
const useCompaniesTab = ({
    isActive,
    companyOptions,
    setCompanyOptions,
    companyById,
    userOptions,
    getCompanyMembershipPermission,
    saving,
    setSaving,
    showMessage,
    loadingUsers,
    userLoadError,
}) => {
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [companyDraftMode, setCompanyDraftMode] = useState('view');
    const [companyDraft, setCompanyDraft] = useState(createEmptyCompanyDraft);

    const selectedCompany = useMemo(
        () => companyOptions.find((company) => company.id === selectedCompanyId) || null,
        [companyOptions, selectedCompanyId],
    );
    const companyUserCountById = useMemo(() => {
        const next = new Map();
        companyOptions.forEach((company) => next.set(company.id, 0));
        userOptions.forEach((user) => {
            user.companies.forEach((companyId) => {
                next.set(companyId, (next.get(companyId) || 0) + 1);
            });
        });
        return next;
    }, [companyOptions, userOptions]);
    const selectedCompanyUserIds = useMemo(() => {
        if (!selectedCompany) return [];
        return userOptions
            .filter((user) => user.companies.includes(selectedCompany.id))
            .map((user) => user.id);
    }, [selectedCompany, userOptions]);
    const isCreatingCompany = companyDraftMode === 'create';
    const companyFormTitle = isCreatingCompany ? 'Ny virksomhed' : 'Rediger virksomhed';

    const getDraftCompanyMembershipPermission = useCallback((userId) => {
        return normalizeCompanyMembershipPermissionEntry(companyDraft.permissionsByUserId?.[userId]);
    }, [companyDraft.permissionsByUserId]);

    useEffect(() => {
        if (!isActive) return;
        if (companyDraftMode !== 'view') return;
        if (selectedCompanyId && companyById.has(selectedCompanyId)) return;
        setSelectedCompanyId(companyOptions[0]?.id || '');
    }, [companyById, companyDraftMode, companyOptions, isActive, selectedCompanyId]);

    const startCompanyCreate = () => {
        setSelectedCompanyId('');
        setCompanyDraft(createEmptyCompanyDraft());
        setCompanyDraftMode('create');
    };

    const startCompanyEdit = () => {
        if (!selectedCompany) return;
        if (loadingUsers) {
            showMessage('Brugerlisten hentes stadig.', 'warning');
            return;
        }

        const userIds = selectedCompanyUserIds;
        const permissionsByUserId = userIds.reduce((next, userId) => {
            next[userId] = getCompanyMembershipPermission(userId, selectedCompany.id);
            return next;
        }, {});

        setCompanyDraft({
            name: selectedCompany.label || '',
            testCompany: selectedCompany.testCompany === true,
            active: selectedCompany.active !== false,
            userIds,
            permissionsByUserId,
        });
        setCompanyDraftMode('edit');
    };

    const cancelCompanyDraft = () => {
        setCompanyDraft(createEmptyCompanyDraft());
        setCompanyDraftMode('view');
    };

    const updateCompanyDraftUserIds = (userIds) => {
        const normalizedUserIds = normalizeIdList(userIds);
        setCompanyDraft((currentDraft) => {
            const nextPermissions = {};

            normalizedUserIds.forEach((userId) => {
                nextPermissions[userId] = normalizeCompanyMembershipPermissionEntry(
                    currentDraft.permissionsByUserId?.[userId] ||
                    getCompanyMembershipPermission(userId, selectedCompanyId),
                );
            });

            return {
                ...currentDraft,
                userIds: normalizedUserIds,
                permissionsByUserId: nextPermissions,
            };
        });
    };

    const updateCompanyDraftMembershipPermission = (userId, key, value) => {
        setCompanyDraft((currentDraft) => ({
            ...currentDraft,
            permissionsByUserId: {
                ...currentDraft.permissionsByUserId,
                [userId]: {
                    ...normalizeCompanyMembershipPermissionEntry(
                        currentDraft.permissionsByUserId?.[userId],
                    ),
                    [key]: value,
                },
            },
        }));
    };

    const saveCompanyDraft = async () => {
        if (!isCreatingCompany && !selectedCompany) return;
        if (loadingUsers) {
            showMessage('Brugerlisten hentes stadig.', 'warning');
            return;
        }
        if (userLoadError) {
            showMessage('Brugerlisten skal kunne hentes, før brugertilknytninger kan gemmes.', 'warning');
            return;
        }

        const companyName = String(companyDraft.name || '').trim();
        if (isCreatingCompany && !companyName) {
            showMessage('Navn er påkrævet.', 'warning');
            return;
        }
        const testCompany = companyDraft.testCompany === true;
        const testCompanyChanged = !isCreatingCompany && selectedCompany.testCompany !== testCompany;
        const active = companyDraft.active !== false;
        const activeChanged = !isCreatingCompany && (selectedCompany.active !== false) !== active;

        const companyRef = isCreatingCompany
            ? doc(collection(db, 'companies'))
            : doc(db, 'companies', selectedCompany.id);
        const companyId = companyRef.id;
        const nextUserIds = new Set(normalizeIdList(companyDraft.userIds));
        const batch = writeBatch(db);
        let changedUserCount = 0;

        if (isCreatingCompany) {
            batch.set(companyRef, {
                name: companyName,
                test_company: testCompany,
                active,
            });
        } else if (testCompanyChanged || activeChanged) {
            const companyUpdate = {};
            if (testCompanyChanged) companyUpdate.test_company = testCompany;
            if (activeChanged) companyUpdate.active = active;
            batch.set(companyRef, companyUpdate, { merge: true });
        }

        userOptions.forEach((user) => {
            const currentlyConnected = user.companies.includes(companyId);
            const shouldBeConnected = nextUserIds.has(user.id);

            const currentPermission = normalizeCompanyMembershipPermissionEntry(
                user.companyMemberships?.[companyId],
            );
            const nextPermission = normalizeCompanyMembershipPermissionEntry(
                companyDraft.permissionsByUserId?.[user.id],
            );

            const membershipChanged = currentlyConnected !== shouldBeConnected;
            const permissionChanged =
                shouldBeConnected &&
                (!currentlyConnected || !areCompanyMembershipPermissionsEqual(currentPermission, nextPermission));

            if (!membershipChanged && !permissionChanged) return;

            changedUserCount += 1;

            const nextMemberships = { ...user.companyMemberships };
            if (shouldBeConnected) {
                nextMemberships[companyId] = serializeCompanyMembershipPermissionEntry(nextPermission);
            } else {
                delete nextMemberships[companyId];
            }

            const serializedMemberships = Object.entries(nextMemberships).reduce(
                (next, [membershipCompanyId, membershipPermission]) => {
                    next[membershipCompanyId] = serializeCompanyMembershipPermissionEntry(
                        membershipPermission,
                    );
                    return next;
                },
                {},
            );

            const userUpdatePayload = {
                company_memberships:
                    Object.keys(serializedMemberships).length > 0
                        ? serializedMemberships
                        : deleteField(),
            };

            if (membershipChanged) {
                userUpdatePayload.companies = shouldBeConnected
                    ? arrayUnion(companyId)
                    : arrayRemove(companyId);
            }

            batch.set(
                doc(db, 'users', user.id),
                userUpdatePayload,
                { merge: true },
            );
        });

        if (!isCreatingCompany && changedUserCount === 0 && !testCompanyChanged && !activeChanged) {
            setCompanyDraftMode('view');
            showMessage('Ingen ændringer at gemme.');
            return;
        }

        setSaving(true);
        try {
            await batch.commit();
            if (isCreatingCompany) {
                setCompanyOptions((currentCompanies) => [
                    ...currentCompanies,
                    {
                        id: companyId,
                        label: companyName,
                        testCompany,
                        active,
                        wasteReceiver: '',
                    },
                ].sort((a, b) => a.label.localeCompare(b.label)));
                setSelectedCompanyId(companyId);
            } else if (testCompanyChanged || activeChanged) {
                setCompanyOptions((currentCompanies) =>
                    currentCompanies.map((company) =>
                        company.id === companyId
                            ? { ...company, testCompany, active }
                            : company,
                    ),
                );
            }
            setCompanyDraft(createEmptyCompanyDraft());
            setCompanyDraftMode('view');
            showMessage(isCreatingCompany ? 'Virksomhed oprettet.' : 'Virksomhedsbrugere gemt.');
        } catch (error) {
            console.error('Error saving company memberships:', error);
            const errorCode = String(error?.code || '').trim();
            const errorMessage = errorCode
                ? `Kunne ikke gemme virksomhed. (${errorCode})`
                : 'Kunne ikke gemme virksomhed.';
            showMessage(errorMessage, 'error');
        } finally {
            setSaving(false);
        }
    };

    const reset = useCallback(() => {
        setCompanyDraft(createEmptyCompanyDraft());
        setCompanyDraftMode('view');
    }, []);

    return {
        cancelCompanyDraft,
        companyDraft,
        companyDraftMode,
        companyFormTitle,
        companyUserCountById,
        getDraftCompanyMembershipPermission,
        isCreatingCompany,
        reset,
        saveCompanyDraft,
        selectedCompany,
        selectedCompanyId,
        selectedCompanyUserIds,
        setCompanyDraft,
        setSelectedCompanyId,
        startCompanyCreate,
        startCompanyEdit,
        updateCompanyDraftMembershipPermission,
        updateCompanyDraftUserIds,
    };
};

export default useCompaniesTab;
export { createEmptyCompanyDraft };
